/// <amd-module name="Build/OrgSettings/SettingsView" />
import * as React from "react";

import { OrgSettingsData } from "Build/Client/Contracts/DataProvider";
import { IOrgSettingsService } from "Build/OrgSettings/OrgSettingsService";
import * as Resources from "Build/OrgSettings/Resources";
import { SettingsToggle } from "Build/SettingsToggle/SettingsToggle";

import { getAssetLocation } from "VSS/Platform/Location";
import { Card } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { Header, TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ZeroData } from "VSSUI/ZeroData";

import { BestPracticeSettings, CommentTriggerOption, DisabledSettings, ForkProtectionOption } from "Build/Common/Library/PipelineTriggerSettings";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

class PipelineTriggerSettingsHelper {
    private static areSettingsBestPractice(settings: OrgSettingsData): boolean {
        return (
            settings.buildsEnabledForForks &&
            settings.enforceJobAuthScopeForForks &&
            settings.enforceNoAccessToSecretsFromForks &&
            settings.isCommentRequiredForPullRequest &&
            !settings.requireCommentsForNonTeamMemberAndNonContributors &&
            !settings.requireCommentsForNonTeamMembersOnly
        );
    }

    private static areBuildsDisabled(settings: OrgSettingsData): boolean {
        return !settings.buildsEnabledForForks;
    }

    static getBuildProtectionOption(settings: OrgSettingsData): ForkProtectionOption {
        return this.areBuildsDisabled(settings)
            ? ForkProtectionOption.Disabled
            : this.areSettingsBestPractice(settings)
            ? ForkProtectionOption.BestPractice
            : ForkProtectionOption.Custom;
    }

    static getCommentRequirement(settings: OrgSettingsData): CommentTriggerOption {
        return !settings.requireCommentsForNonTeamMembersOnly && !settings.requireCommentsForNonTeamMemberAndNonContributors
            ? CommentTriggerOption.All
            : settings.requireCommentsForNonTeamMembersOnly
            ? CommentTriggerOption.NonTeamMembers
            : CommentTriggerOption.NonTeamMembersNonContributor;
    }
}

interface IPipelineTriggerSettingsState {
    selectedProtectionOption: ForkProtectionOption | undefined;
}

class PipelineOrgSettingsView extends VssComponent<IVssComponentProperties, IPipelineTriggerSettingsState> {
    private _service: IOrgSettingsService;
    private _settings: IReadonlyObservableValue<OrgSettingsData>;
    private _hasNoPermission: IReadonlyObservableValue<boolean>;
    private _checkAudit: boolean;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IOrgSettingsService>("IOrgSettingsService");
        this._settings = this._service.data;
        this._hasNoPermission = this._service.hasNoPermission;
        this._checkAudit = isFeatureFlagEnabled(this.context.pageContext, "AzureDevOps.BuildAndRelease.ShowAuditLogForHighRiskSettings", false);
        this.state = {
            selectedProtectionOption: undefined
        };
    }

    private _renderGeneralSettingsCard(observerProps: { settings: OrgSettingsData }): JSX.Element {
        return (
            <Card
                className="bolt-card-white pipelines-settings-card"
                titleProps={{ text: Resources.General, size: TitleSize.Small }}
                contentProps={{ contentPadding: false }}
            >
                <div className="settings-view">
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.statusBadgesArePrivate}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.BadgesArePrivate}
                            description={Resources.BadgesArePrivateToolTip}
                            onChange={(e, checked) => this._service.updateBadgesArePrivate(checked)}
                        />
                    </div>
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.enforceSettableVar}
                            auditChecked={this._checkAudit && observerProps.settings.auditEnforceSettableVar}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            auditDisabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.EnforceVariable}
                            description={Resources.EnforceVariableToolTip}
                            onChange={(e, checked) => this._service.updateEnforceSettableVar(checked)}
                            onAuditChange={(e, auditChecked) => this._service.updateAuditEnforceSettableVar(auditChecked)}
                            isHighRisk={true}
                            isHighRiskAudit={this._checkAudit}
                            learnMoreLink="https://go.microsoft.com/fwlink/?linkid=2214054"
                        />
                    </div>
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.enforceJobAuthScope}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.EnforceJobAuthScope}
                            description={Resources.EnforceJobAuthScopeToolTip}
                            onChange={(e, checked) => this._service.updateJobAuthScope(checked)}
                        />
                    </div>
                    {isFeatureFlagEnabled(this.context.pageContext, "AzureDevOps.ReleaseManagement.EnforceJobAuthScopeForReleases", false) && (
                        <div>
                            <SettingsToggle
                                checked={observerProps.settings.enforceJobAuthScopeForReleases}
                                disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                title={Resources.EnforceJobAuthScopeForReleases}
                                description={Resources.EnforceJobAuthScopeForReleasesToolTip}
                                onChange={(e, checked) => this._service.updateJobAuthScopeForReleases(checked)}
                            />
                        </div>
                    )}
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.enforceReferencedRepoScopedToken}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.EnforceReferencedRepoScopedToken}
                            description={Resources.EnforceReferencedRepoScopedTokenToolTip}
                            onChange={(e, checked) => this._service.updateEnforceReferencedRepoScopedToken(checked)}
                        />
                    </div>
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.disableStageChooser}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.DisableStageChooser}
                            description={Resources.DisableStageChooserToolTip}
                            onChange={(e, checked) => this._service.updateDisableStageChooser(checked)}
                        />
                    </div>
                    {isFeatureFlagEnabled(this.context.pageContext, "Build2.NewTogglesToDisableClassicPipelineCreation", false) && (
                        <div>
                            <div>
                                <SettingsToggle
                                    checked={observerProps.settings.disableClassicBuildPipelineCreation}
                                    disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                    title={Resources.DisableClassicBuildPipelineCreation}
                                    description={Resources.DisableClassicBuildPipelineCreationTooltip}
                                    onChange={(e, checked) => this._service.updateDisableClassicBuildPipelineCreation(checked)}
                                />
                            </div>
                            <div>
                                <SettingsToggle
                                    checked={observerProps.settings.disableClassicReleasePipelineCreation}
                                    disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                    title={Resources.DisableClassicReleasePipelineCreation}
                                    description={Resources.DisableClassicReleasePipelineCreationTooltip}
                                    onChange={(e, checked) => this._service.updateDisableClassicReleasePipelineCreation(checked)}
                                />
                            </div>
                        </div>
                    )}
                    {!isFeatureFlagEnabled(this.context.pageContext, "Build2.NewTogglesToDisableClassicPipelineCreation", false) && (
                        <div>
                            <SettingsToggle
                                checked={observerProps.settings.disableClassicPipelineCreation}
                                disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                title={Resources.DisableClassicPipelineCreation}
                                description={Resources.DisableClassicPipelineCreationTooltip}
                                onChange={(e, checked) => this._service.updateDisableClassicPipelineCreation(checked)}
                            />
                        </div>
                    )}
                </div>
            </Card>
        );
    }

    private _renderForkProtectionToggle(settings: OrgSettingsData): JSX.Element {
        var handleForkProtectionToggle = (checked: boolean) => {
            this._service.updatePipelineTriggerSettings({ forkProtectionEnabled: checked });
            // reset the selectedProtectionOption
            this.setState({ selectedProtectionOption: ForkProtectionOption.Disabled });
        };

        return (
            <SettingsToggle
                checked={settings.forkProtectionEnabled}
                disabled={!settings.hasManagePipelinePoliciesPermission}
                title={Resources.ForkProtectionEnabled}
                description={Resources.ForkProtectionEnabledTooltip}
                onChange={(e, checked) => handleForkProtectionToggle(checked)}
                isHighRisk
                highRiskTitle={Resources.ForkProtectionEnabledToggleHighRiskTitle}
                learnMoreLink="https://go.microsoft.com/fwlink/?linkid=862029"
            />
        );
    }

    private _renderDisableImpliedYAMLCiTrigger(settings: OrgSettingsData): JSX.Element {
        return (
            <div>
                <SettingsToggle
                    checked={settings.disableImpliedYAMLCiTrigger}
                    title={Resources.DisableImpliedYAMLCiTriggerTitle}
                    description={Resources.DisableImpliedYAMLCiTriggerDescription}
                    onChange={(e, checked) => this._service.updateDisableImpliedYAMLCiTrigger(checked)}
                    disabled={!settings.hasManagePipelinePoliciesPermission}
                />
            </div>
        );
    }

    private _handleCommentRequirementRadioButtonSelection = (settings: OrgSettingsData, selectedId: string) => {
        if (selectedId === PipelineTriggerSettingsHelper.getCommentRequirement(settings)) {
            return;
        }

        switch (selectedId) {
            case CommentTriggerOption.All: {
                this._service.updatePipelineTriggerSettings({
                    requireCommentsForNonTeamMembersOnly: false,
                    requireCommentsForNonTeamMemberAndNonContributors: false
                });
                break;
            }
            case CommentTriggerOption.NonTeamMembers: {
                this._service.updatePipelineTriggerSettings({
                    requireCommentsForNonTeamMembersOnly: true,
                    requireCommentsForNonTeamMemberAndNonContributors: false
                });
                break;
            }
            case CommentTriggerOption.NonTeamMembersNonContributor: {
                this._service.updatePipelineTriggerSettings({
                    requireCommentsForNonTeamMembersOnly: false,
                    requireCommentsForNonTeamMemberAndNonContributors: true
                });
                break;
            }
        }
    };

    private _renderCommentRequirementSettings(settings: OrgSettingsData): JSX.Element {
        return (
            <div className="flex row margin-left-16 padding-left-16 margin-top-4">
                <RadioButtonGroup
                    selectedButtonId={PipelineTriggerSettingsHelper.getCommentRequirement(settings)}
                    onSelect={selectedId => {
                        this._handleCommentRequirementRadioButtonSelection(settings, selectedId);
                    }}
                >
                    <RadioButton
                        id={CommentTriggerOption.All}
                        text={Resources.RequireCommentsForAllPullRequests}
                        disabled={!settings.hasManagePipelinePoliciesPermission}
                    />
                    <RadioButton id={CommentTriggerOption.NonTeamMembers} text={Resources.RequireCommentsForNonTeamMembersOnly} />
                    <RadioButton
                        id={CommentTriggerOption.NonTeamMembersNonContributor}
                        text={Resources.RequireCommentsForNonTeamMemberAndNonContributors}
                        disabled={!settings.hasManagePipelinePoliciesPermission}
                    />
                </RadioButtonGroup>
            </div>
        );
    }

    private _renderCustomForkProtectionLevelSettings(settings: OrgSettingsData): JSX.Element {
        return (
            <div>
                <Checkbox
                    onChange={(_, enforceNoAccessToSecretsFromForks) =>
                        this._service.updatePipelineTriggerSettings({
                            enforceNoAccessToSecretsFromForks
                        })
                    }
                    label={Resources.EnforceNoAccessToSecretsFromForks}
                    checked={settings.enforceNoAccessToSecretsFromForks}
                    disabled={!settings.hasManagePipelinePoliciesPermission}
                />
                <Checkbox
                    onChange={(_, enforceJobAuthScopeForForks) => this._service.updatePipelineTriggerSettings({ enforceJobAuthScopeForForks })}
                    label={Resources.EnforceJobAuthScopeForForks}
                    checked={settings.enforceJobAuthScopeForForks}
                    disabled={!settings.hasManagePipelinePoliciesPermission}
                />
                <Checkbox
                    onChange={(_, isCommentRequiredForPullRequest) =>
                        this._service.updatePipelineTriggerSettings({ isCommentRequiredForPullRequest })
                    }
                    label={Resources.IsCommentRequiredForPullRequest}
                    checked={settings.isCommentRequiredForPullRequest}
                    disabled={!settings.hasManagePipelinePoliciesPermission}
                />
                {settings.isCommentRequiredForPullRequest && this._renderCommentRequirementSettings(settings)}
                <div></div>
            </div>
        );
    }

    private _renderForkProtectionLevelSettings(settings: OrgSettingsData): JSX.Element {
        var { selectedProtectionOption } = this.state;

        // Set the initial state of the option
        if (selectedProtectionOption === undefined) {
            this.setState({ selectedProtectionOption: PipelineTriggerSettingsHelper.getBuildProtectionOption(settings) });
        }

        var handleForkProtectionLevelRadioButtonSelection = (selectedId: string) => {
            if (selectedId === selectedProtectionOption) {
                return;
            }

            var newSetting = BestPracticeSettings;

            switch (selectedId) {
                case ForkProtectionOption.Disabled: {
                    this.setState({ selectedProtectionOption: ForkProtectionOption.Disabled });
                    newSetting = DisabledSettings;
                    break;
                }
                case ForkProtectionOption.BestPractice: {
                    this.setState({ selectedProtectionOption: ForkProtectionOption.BestPractice });
                    break;
                }
                case ForkProtectionOption.Custom: {
                    this.setState({ selectedProtectionOption: ForkProtectionOption.Custom });
                    break;
                }
            }

            this._service.updatePipelineTriggerSettings(newSetting);
        };

        return (
            <div className="github-fork-protection-settings flex-row padding-horizontal-20">
                <div className="flex-column margin-right-16 padding-right-16 base-toggle-width"></div>
                <div className="flex-column flex-grow margin-left-16 padding-horizontal-16">
                    <RadioButtonGroup
                        selectedButtonId={selectedProtectionOption}
                        onSelect={selectedId => {
                            handleForkProtectionLevelRadioButtonSelection(selectedId);
                        }}
                    >
                        <RadioButton
                            id={ForkProtectionOption.Disabled}
                            text={Resources.DisableBuildsInPullRequestsFromForkedRepositories}
                            disabled={!settings.hasManagePipelinePoliciesPermission}
                            ariaDescribedBy="forkProtectionOption-disabled"
                        />
                        <div className="body-s margin-left-16 margin-bottom-2 padding-left-16" aria-labelledby="forkProtectionOption-disabled">
                            <span id="forkProtectionOption-disabled" className="secondary-text">
                                {Resources.DisableBuildsInPullRequestsFromForkedRepositoriesTooltip}
                            </span>
                        </div>
                        <RadioButton
                            id={ForkProtectionOption.BestPractice}
                            text={Resources.SecurelyBuildPullRequestsFromForkedRepositories}
                            disabled={!settings.hasManagePipelinePoliciesPermission}
                            ariaDescribedBy="forkProtectionOption-secure"
                        />
                        <div className="body-s margin-left-16 margin-bottom-4 padding-left-16" aria-labelledby="forkProtectionOption-secure">
                            <span id="forkProtectionOption-secure" className="secondary-text">
                                {Resources.SecurelyBuildPullRequestsFromForkedRepositoriesTooltip}
                            </span>
                        </div>
                        <RadioButton id={ForkProtectionOption.Custom} text={Resources.CustomizeRulesOfBuildingPullRequestsFromForkedRepositories} />
                        <div className="flex row margin-left-16 padding-left-16 margin-top-4 full-width">
                            {selectedProtectionOption == ForkProtectionOption.Custom && this._renderCustomForkProtectionLevelSettings(settings)}
                        </div>
                    </RadioButtonGroup>
                </div>
            </div>
        );
    }

    private _renderTriggerSettingsCard(observerProps: { settings: OrgSettingsData }): JSX.Element | null {
        const isAllowCentralizedPipelineControlsEnabled = isFeatureFlagEnabled(this.context.pageContext, "Build2.AllowCentralizedPipelineControls", false);
        const isDisableImpliedYAMLCiTriggerFeatureEnabled = isFeatureFlagEnabled(this.context.pageContext, "Build2.DisableImpliedYAMLCiTrigger", false);
        if (!isAllowCentralizedPipelineControlsEnabled && !isDisableImpliedYAMLCiTriggerFeatureEnabled) {
            return null;
        }
        
        const settings = observerProps.settings;

        return (
            <Card
                className="bolt-card-white pipelines-settings-card"
                titleProps={{ text: Resources.Triggers, size: TitleSize.Small }}
                contentProps={{ contentPadding: false }}
            >
                <div className="settings-view">
                    {
                        isAllowCentralizedPipelineControlsEnabled &&
                        <div>
                            {this._renderForkProtectionToggle(settings)}
                            {settings.forkProtectionEnabled && this._renderForkProtectionLevelSettings(settings)}
                        </div>
                    }
                    {
                        isDisableImpliedYAMLCiTriggerFeatureEnabled &&
                        <div>
                            {this._renderDisableImpliedYAMLCiTrigger(settings)}
                        </div>
                    }
                </div>
            </Card>
        );
    }

    private _renderTaskRestrictionSettingsCard(observerProps: { settings: OrgSettingsData }): JSX.Element {
        return (
            <Card
                className="bolt-card-white pipelines-settings-card"
                titleProps={{ text: Resources.TaskLockdown, size: TitleSize.Small }}
                contentProps={{ contentPadding: false }}
            >
                <div className="settings-view">
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.disableInBoxTasksVar}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.DisableInBoxTasks}
                            description={Resources.DisableInBoxTasksTooltip}
                            onChange={(e, checked) => this._service.updateDisableInBoxTasksVar(checked)}
                        />
                    </div>
                    <div>
                        <SettingsToggle
                            checked={observerProps.settings.disableMarketplaceTasksVar}
                            disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                            title={Resources.DisableMarketplaceTasks}
                            description={Resources.DisableMarketplaceTasksTooltip}
                            onChange={(e, checked) => this._service.updateDisableMarketplaceTasksVar(checked)}
                        />
                    </div>
                    {isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.Node6LockdownAllowed", false) && (
                        <div>
                            <SettingsToggle
                                checked={observerProps.settings.disableNode6TasksVar}
                                disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                title={Resources.DisableNode6Tasks}
                                description={Resources.DisableNode6TasksTooltip}
                                onChange={(e, checked) => this._service.updateDisableNode6TasksVar(checked)}
                            />
                        </div>
                    )}
                    {isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.AllowEnableShellTasksArgsSanitizingToggle", false) &&
                        <div>
                            <SettingsToggle
                                checked={observerProps.settings.enableShellTasksArgsSanitizing}
                                disabled={!observerProps.settings.hasManagePipelinePoliciesPermission}
                                title={Resources.EnableShellTasksArgsSanitizing}
                                description={Resources.EnableShellTasksArgsSanitizingToolTip}
                                onChange={(e, checked) => this._service.updateEnableShellTasksArgsSanitizing(checked)}
                                learnMoreLink="https://aka.ms/ado/75787"
                            />
                        </div>}
                </div>
            </Card>
        );
    }

    private _renderSettings(observerProps: { settings: OrgSettingsData, hasNoPermission: boolean }): JSX.Element {
        const settingsLength = observerProps.settings !== null ? Object.keys(observerProps.settings).length : 0;
        // If settings haven't fetched yet, show a spinner
        if (!observerProps.hasNoPermission && settingsLength === 0) {
            return (
                <div className="bolt-page-grey org-settings flex flex-column flex-grow">
                    <Spinner className="loading-spinner" label={Resources.LoadingText} />;
                </div>
            );
        }

        // If settings have fetched, but access is denied, show error
        if (observerProps.hasNoPermission && settingsLength === 0) {
            return (
                <div className="bolt-page-grey org-settings flex flex-column flex-grow">
                <Header title={Resources.Settings} titleSize={TitleSize.Medium} />
                <Card className="bolt-card-white pipelines-settings-card org-settings-zero-data-card">
                    <ZeroData
                        className="flex-grow org-settings-zero-data"
                        primaryText={Resources.OrgSettingsNoPermissionsPrimaryText}
                        secondaryText={Resources.OrgSettingsNoPermissionsSecondaryText}
                        imagePath={getAssetLocation(this.context.pageContext, "ms.vss-build-web/org-settings/Content/nopermission.svg")}
                        imageAltText={""}                        
                    />
                </Card>
            </div>
            );
        }

        return (
            <div className="bolt-page-grey org-settings flex flex-column flex-grow">
                <Header title={Resources.Settings} titleSize={TitleSize.Medium} />
                {this._renderGeneralSettingsCard(observerProps)}
                {this._renderTriggerSettingsCard(observerProps)}
                {observerProps.settings.isTaskLockdownFeatureEnabled && this._renderTaskRestrictionSettingsCard(observerProps)}
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <Observer settings={this._settings} hasNoPermission={this._hasNoPermission}>
                        {(observerProps: { settings: OrgSettingsData, hasNoPermission: boolean }) => {
                            return this._renderSettings(observerProps);
                        }}
                    </Observer>
                </Page>
            </Surface>
        );
    }
}

Components.add("pipelines-org-settings-view", PipelineOrgSettingsView);
